import React from 'react';
import Popup from '../Popup/Popup';
import { Rating } from '@mui/material';
import { submitBookingFeedback } from '../../api/bookings';
import { useLoader } from '../../hooks/LoaderContext';

interface FeedbackPopupProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string | null;
  candidateName: string;
}

const FeedbackPopup: React.FC<FeedbackPopupProps> = ({ isOpen, onClose, bookingId, candidateName }) => {
  const { setIsLoading } = useLoader();
  const [rating, setRating] = React.useState<number | null>(0);
  const [feedback, setFeedback] = React.useState('');
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleRatingChange = (_event: React.SyntheticEvent, newValue: number | null) => {
    setRating(newValue);
  };

  const handleConfirm = async () => {
    if (!bookingId) return;

    try {
      setIsLoading(true);
      setError(null);

      await submitBookingFeedback(bookingId, {
        Rating: rating || undefined,
        Feedback: feedback || undefined
      });

      setIsSubmitted(true);
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setFeedback('');
    setIsSubmitted(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Popup width="33.8542vw" onClose={handleClose}>
      {isSubmitted ? (
        <h2 className="popup-title">Thank you for your feedback about <span className="popup-title-name">{candidateName.split(' ')[0]}</span>!</h2>
      ) : (
        <>
          <h2 className="popup-title">Successfully accepted!</h2>
          <p>
            The Timesheet has been accepted and will now show as completed in your Timesheet dashboard.
            If you have any questions about this, please reach out to a member of our team for further assistance.
          </p>
          <h3>How was your experience <br /> with {candidateName.split(' ')[0]}?</h3>
          <Rating
            name="feedback-rating"
            value={rating}
            onChange={handleRatingChange}
            precision={1}
            size="large"
            sx={{
              '& .MuiRating-iconFilled': {
                color: '#A27A25',
              },
              '& .MuiRating-iconHover': {
                color: '#A27A25',
              },
              '& .MuiRating-iconEmpty': {
                color: '#DED8E5'
              }
            }}
          />
          <textarea 
            placeholder="Please share your feedback here..." 
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          {error && <p className="error-message">{error}</p>}
          <div className="btns btns-feedback">
            <button className="btn btn-confirm" onClick={handleConfirm}>Confirm</button>
          </div>
        </>
      )}
    </Popup>
  );
};

export default FeedbackPopup;
